import { stateText, type AvailabilityTone } from "@/lib/acd";
import type { AppUser, FloorRep } from "@/lib/types";

/**
 * Who a name on a record is, and where they are now (ACL-508): the person card a supervisor sees
 * over a rep's name on the timeline. A name comes three ways: with the person's account id (a note,
 * a stage move, a callback someone set), with their phone (`extension@domain`, the rep on a call),
 * or as a name alone (the rep a callback is kept for).
 */
export interface PersonRef {
  userId?: number | null;
  /** The rep's phone, `extension@domain`, as a call names it. */
  phone?: string | null;
  /** The words the record shows. */
  name: string;
}

/** The account a name stands for, among the people the reader may see; null for none, or for a
 *  name alone that two accounts share (nothing is guessed). */
export function findPerson(users: readonly AppUser[] | undefined, who: PersonRef): AppUser | null {
  if (!users) return null;
  if (who.userId != null) return users.find((u) => u.id === who.userId) ?? null;
  if (who.phone) {
    const [extension, domain] = who.phone.split("@");
    const found = users.find(
      (u) =>
        (u.sip_endpoint_extension === extension || u.sip_endpoint_numbers.includes(extension!)) &&
        (!domain || !u.sip_endpoint_domain_name || u.sip_endpoint_domain_name === domain),
    );
    if (found) return found;
  }
  const named = users.filter((u) => (u.display_name ?? u.email) === who.name);
  return named.length === 1 ? named[0]! : null;
}

/** The person's place on the live floor, by their phone: absent for one the ACD has not signed in. */
export function floorRepOf(reps: readonly FloorRep[] | undefined, user: AppUser): FloorRep | null {
  const extension = user.sip_endpoint_extension;
  if (!reps || !extension) return null;
  return (
    reps.find(
      (r) =>
        (r.user === extension || user.sip_endpoint_numbers.includes(r.user)) &&
        (!user.sip_endpoint_domain_name || r.domain === user.sip_endpoint_domain_name),
    ) ?? null
  );
}

/** The five words the card says a person is: online, on a call, busy, on a break, offline. */
export type PersonStatus = "online" | "on_call" | "busy" | "break" | "offline";

const STATUS: Record<PersonStatus, { label: string; tone: AvailabilityTone }> = {
  online: { label: "Online", tone: "success" },
  on_call: { label: "On a call", tone: "accent" },
  busy: { label: "Busy", tone: "warning" },
  break: { label: "On a break", tone: "warning" },
  offline: { label: "Offline", tone: "neutral" },
};

const OF_STATE: Record<string, PersonStatus> = {
  available: "online",
  ringing: "on_call",
  busy: "on_call",
  dialing: "on_call",
  busy_external: "on_call",
  disposition: "busy",
  wrapup: "busy",
  paused: "break",
  offline: "offline",
};

/**
 * Where a person is now, by their floor record: one of the five, and the floor's own words for it
 * when they say more ("Choosing a disposition", "On a break · Lunch"). No record is offline: the
 * ACD has not signed them in. A state this console does not know reads as busy, in the ACD's words.
 */
export function personStatus(rep: Pick<FloorRep, "state" | "code"> | null): {
  status: PersonStatus;
  label: string;
  tone: AvailabilityTone;
  detail: string | null;
} {
  if (!rep) return { status: "offline", ...STATUS.offline, detail: "Not signed in to the phone" };
  const status = OF_STATE[rep.state] ?? "busy";
  const words = stateText(rep.state, rep.code).label;
  const detail =
    status === "break"
      ? (words.split(" · ")[1] ?? null)
      : words === STATUS[status].label
        ? null
        : words;
  return { status, ...STATUS[status], detail };
}
